import React, { useEffect, useState, useContext } from "react";
import { format } from "date-fns";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { StatusIndicator } from "../../../components/statusIndicator";
import FormatPrice from "../../../utils/FormatPrice";

// Maps a Stripe invoice status to a StatusIndicator variant.
const statusType = (status) => {
  switch (status) {
    case "paid":
      return "success";
    case "open":
      return "warning";
    case "void":
    case "uncollectible":
      return "danger";
    default:
      return "default";
  }
};

function BillingHistory() {
  const { authContext } = useContext(AuthContext);
  const authFetch = useAuthFetch();

  const {
    organization: { orgId },
  } = authContext;

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) return;

    const fetchInvoices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await authFetch(
          `${process.env.REACT_APP_API_URL}/v1/organizations/${orgId}/subscription/invoices`,
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data = await response.json();
        setInvoices(data.invoices || []);
      } catch (err) {
        console.error("Error fetching invoices:", err);
        setError("We couldn't load your billing history. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [orgId]);

  if (isLoading) {
    return <p className="text-gray-600 dark:text-gray-300">Loading...</p>;
  }

  if (error) {
    return <p className="text-danger">{error}</p>;
  }

  if (invoices.length === 0) {
    return (
      <p className="text-gray-600 dark:text-gray-300">
        You don't have any bills yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr className="text-gray-500 dark:text-gray-400 uppercase text-xsm tracking-wide">
            <th className="py-3 pr-4 text-left font-medium">Date</th>
            <th className="py-3 px-4 text-left font-medium">Invoice</th>
            <th className="py-3 px-4 text-left font-medium">Amount</th>
            <th className="py-3 px-4 text-left font-medium">Status</th>
            <th className="py-3 pl-4 text-right font-medium">
              <span className="sr-only">View</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-sm text-gray-600 dark:text-gray-300">
          {invoices.map((invoice) => (
            <tr key={invoice.id}>
              <td className="py-3 pr-4 whitespace-nowrap">
                {format(new Date(invoice.created * 1000), "MMM d, yyyy")}
              </td>
              <td className="py-3 px-4 whitespace-nowrap">
                {invoice.number || "—"}
              </td>
              <td className="py-3 px-4 whitespace-nowrap">
                {FormatPrice(invoice.total, invoice.currency)}
              </td>
              <td className="py-3 px-4 whitespace-nowrap">
                <span className="inline-block">
                  <StatusIndicator
                    type={statusType(invoice.status)}
                    text={invoice.status}
                  />
                </span>
              </td>
              <td className="py-3 pl-4 text-right whitespace-nowrap">
                {invoice.hostedInvoiceUrl || invoice.invoicePdf ? (
                  <a
                    href={invoice.hostedInvoiceUrl || invoice.invoicePdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-primary hover:text-brand-200 font-semibold transition-colors duration-200"
                  >
                    View
                  </a>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { BillingHistory };
